import React, { Fragment, useEffect, useState } from 'react';
import { Modal, Form, Row, Col } from 'antd';
import {
  useInfoModalDispatch,
  useInfoModalState,
} from '../../contexts/InfoModalContext';
import { InformationsIcon } from '../../../../resource/icons';
import { editor, fields, filedNames } from './constants';
import { createInitialValues, prepareFormValues } from './utils';
import useInfoMutations from '../../mutations/useInfoMutations';
import styles from './InfoModalForm.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  editorLinkModalDataSelector,
  editorPhoneModalDataSelector,
  editorStateSelector,
} from '../../store/selectors';
import { ContentState, EditorState, convertFromHTML } from 'draft-js';
import MessageEditor, {
  blockHeading,
  blockList,
  decorators,
  informationsLinkAdd,
  initEditorInformationsLinkModalData,
  inlineStyle,
  linkAdd,
  phoneAdd,
} from '../../../../components/MessageEditor';
import {
  setEditorLinkModalData,
  setEditorPhoneModalData,
  setEditorState,
} from '../../store/actions';
import {
  HeaderImgProvider,
  useHeaderImgActions,
} from './components/HeaderImgField';
import { stateToHTML } from 'draft-js-export-html';
import { typeIds } from '../../constants/common';
import Preview from './components/Preview/Preview';
import useAppSectionsQuery from '../../queries/useAppSectionsQuery';

const Title = ({ isEdit }) => {
  return (
    <div className={styles.titleWrap}>
      <InformationsIcon />
      <h3>{isEdit ? 'Изменить информирование' : 'Добавить информирование'}</h3>
    </div>
  );
};

const InfoModalFormBase = ({ form }) => {
  const editorState = useSelector(editorStateSelector);

  const [editorInformationLinkModalData, setEditorInformationLinkModalData] =
    useState(initEditorInformationsLinkModalData);

  const [visiblePreview, setVisiblePreview] = useState(false);

  const { reset: resetImgHeader, getResult: getImgHeaderData } =
    useHeaderImgActions();

  const { validateFields, resetFields, getFieldDecorator, getFieldValue } =
    form;

  const initialData = useInfoModalState();
  const toggleVisible = useInfoModalDispatch();
  const dispatch = useDispatch();

  const { changeInfo, addInfo, loading } = useInfoMutations();

  const { data: appSectionsList = [] } = useAppSectionsQuery();

  const isEdit = Object.keys(initialData || {}).length > 0;

  const initialValues = createInitialValues(initialData);

  const typeValue = getFieldValue(filedNames.typeId);

  const handleChangeVisible = () => {
    setVisiblePreview((prev) => !prev);
  };

  const renderProps = {
    form,
    initialValues,
    infoId: initialData?.id,
    handleChangeVisible,
  };

  const handleCancel = () => {
    toggleVisible(null);
    resetImgHeader();
    setVisiblePreview(false);
    resetFields();
  };

  const handleSubmit = () => {
    validateFields(async (err, values) => {
      if (err) {
        return;
      }

      const { [filedNames.files]: files, ...other } = prepareFormValues(
        values,
        editorState
      );

      const headerImgData = getImgHeaderData();

      isEdit
        ? await changeInfo({
            infoId: initialData?.id,
            values: other,
            files,
            headerImgData,
          })
        : await addInfo({ values: other, files, headerImgData });

      handleCancel();
    });
  };

  const initial = Boolean(initialData);

  useEffect(() => {
    let textForImport = initialValues.text;

    if (textForImport) {
      textForImport = textForImport.replace(
        /<p[^>]*>&nbsp;<\/p>/gi,
        '<p style="margin:0 0 5px 0;">\u200B</p>'
      );
    }

    const contentState = textForImport
      ? ContentState.createFromBlockArray(
          convertFromHTML(textForImport).contentBlocks,
          convertFromHTML(textForImport).entityMap
        )
      : ContentState.createFromText('');
    const newEditorState = EditorState.createWithContent(
      contentState,
      decorators(
        setEditorLinkModalData,
        setEditorInformationLinkModalData,
        setEditorState,
        editorStateSelector,
        setEditorPhoneModalData
      )
    );
    const selection = newEditorState.getSelection();
    const newSelection = selection.merge({
      anchorOffset: 0,
      focusOffset: 0,
    });
    const newEditorStateEnd = EditorState.forceSelection(
      newEditorState,
      newSelection
    );
    dispatch(setEditorState(newEditorStateEnd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  useEffect(() => {
    if (typeValue !== typeIds.advertisement) {
      setVisiblePreview(false);
    }
  }, [typeValue]);

  return (
    <>
      <Modal
        visible={Boolean(initialData)}
        onCancel={handleCancel}
        onOk={handleSubmit}
        okText={isEdit ? 'Изменить' : 'Добавить'}
        title={<Title isEdit={isEdit} />}
        confirmLoading={loading}
        destroyOnClose
        maskClosable={false}
      >
        <Form>
          {fields.map((field, index) => {
            if (!field.children && !field.editor) {
              return (
                <Fragment key={index}>{field.render(renderProps)}</Fragment>
              );
            }
            if (field.editor === editor) {
              return (
                <Form.Item
                  key={index}
                  label="Текст"
                >
                  {getFieldDecorator('text', {
                    initialValues: initialValues.text,
                  })(
                    <div className={styles.itemEditor}>
                      <MessageEditor
                        className={styles.editor}
                        editor={{ editorStateSelector, setEditorState }}
                        toolbarCreate={[
                          inlineStyle,
                          blockHeading,
                          blockList,
                          linkAdd,
                          informationsLinkAdd,
                          phoneAdd,
                        ]}
                        linkModal={{
                          setEditorLinkModalData,
                          editorLinkModalDataSelector,
                        }}
                        phoneModal={{
                          setEditorPhoneModalData,
                          editorPhoneModalDataSelector,
                        }}
                        linkInformationModal={{
                          setEditorInformationLinkModalData,
                          editorInformationLinkModalData,
                        }}
                        appSectionsList={appSectionsList}
                      />
                    </div>
                  )}
                </Form.Item>
              );
            }

            return (
              <Row
                key={index}
                gutter={field.gutter}
              >
                {field.children.map((child, childIndex) => (
                  <Col
                    key={childIndex}
                    span={child.span}
                  >
                    {child.render(renderProps)}
                  </Col>
                ))}
              </Row>
            );
          })}
        </Form>
      </Modal>
      {visiblePreview && (
        <Preview
          form={form}
          htmlContent={stateToHTML(editorState.getCurrentContent())}
        />
      )}
    </>
  );
};

let InfoModalForm = ({ form }) => {
  return (
    <HeaderImgProvider>
      <InfoModalFormBase form={form} />
    </HeaderImgProvider>
  );
};

InfoModalForm = Form.create({ name: 'info_form' })(InfoModalForm);

export default InfoModalForm;
